import axios from 'axios';
import { getStoredToken } from '../../login/api/loginApi';
import { attachAuthInterceptors } from '../../_shared/auth/authInterceptor';
import type {
  StandardTerm,
  ForbiddenWord,
  TerminologySummary,
  StandardTermUpsertRequest,
  ForbiddenWordUpsertRequest,
  StandardTermSearchParams,
  ForbiddenWordSearchParams,
  PageResponse,
  TerminologyExportParams,
  TerminologyDownloadResult,
  ApiError,
  LintRequest,
  LintReportResponse,
} from '../types/terminology';

const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_BASE ?? '/api/eraf',
  headers: { 'Content-Type': 'application/json' },
});
attachAuthInterceptors(apiClient);

// Request interceptor — attach Bearer token
apiClient.interceptors.request.use((config) => {
  const token = getStoredToken();
  if (token) {
    config.headers['Authorization'] = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor — unwrap ApiResponse<T>
apiClient.interceptors.response.use(
  (response) => {
    const d = response.data as Record<string, unknown> | undefined;
    if (d && typeof d === 'object' && 'success' in d) {
      if (d.success === true) {
        return { ...response, data: d['data'] };
      }
      const err = d['error'] as Record<string, unknown> | undefined;
      return Promise.reject({
        code: err?.['code'] ?? `HTTP-${response.status}`,
        message: err?.['message'] ?? 'Unknown error',
        traceId: (err?.['traceId'] as string) ?? response.headers?.['x-trace-id'],
      });
    }
    return response;
  },
  (error) => {
    const errData = error.response?.data?.error as Record<string, unknown> | undefined;
    const code = errData?.['code'] ?? `HTTP-${error.response?.status ?? 0}`;
    const message =
      (errData?.['message'] as string) ??
      (error.response?.data?.message as string) ??
      error.message ??
      'Network error';
    const traceId = (error.response?.headers?.['x-trace-id'] as string) ?? undefined;
    return Promise.reject({ code, message, traceId });
  }
);

// ─── Helpers ────────────────────────────────────────────────────────────────

/**
 * blob 응답은 성공/실패 본문이 동일하게 Blob 이 되어 apiClient 의 JSON 오류
 * 인터셉터가 무력화되므로, export/starter-pack 호출은 apiClient 를 우회해
 * raw fetch 로 status 를 직접 분기한다 (showcaseApi.exportModuleSource 패턴 재사용).
 */
async function fetchBlob(path: string, fallbackFilename: string): Promise<TerminologyDownloadResult> {
  const base = import.meta.env.VITE_API_BASE ?? '/api/eraf';
  const token = getStoredToken();
  const headers: Record<string, string> = {};
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(`${base}${path}`, { method: 'GET', headers });

  if (!response.ok) {
    let code = `HTTP-${response.status}`;
    let message = `다운로드에 실패했습니다 (HTTP ${response.status})`;
    try {
      const body = (await response.json()) as { error?: { code?: string; message?: string } };
      if (body?.error?.code) code = body.error.code;
      if (body?.error?.message) message = body.error.message;
    } catch {
      // 응답이 JSON 이 아닌 경우 — 기본 메시지 유지
    }
    return Promise.reject({ code, message } as ApiError);
  }

  const disposition = response.headers.get('content-disposition') ?? '';
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition);
  const filename = match ? decodeURIComponent(match[1]) : fallbackFilename;
  const blob = await response.blob();

  return { blob, filename };
}

function toQueryString(params: Record<string, unknown>): string {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== '') {
      sp.set(k, String(v));
    }
  }
  const qs = sp.toString();
  return qs ? `?${qs}` : '';
}

// ─── Standard Terms ──────────────────────────────────────────────────────────

export const terminologyApi = {
  // GET /terminology/standard-terms
  listStandardTerms: (
    params: StandardTermSearchParams = {}
  ): Promise<PageResponse<StandardTerm>> =>
    apiClient
      .get<unknown, { data: PageResponse<StandardTerm> }>(
        `/terminology/standard-terms${toQueryString(params as Record<string, unknown>)}`
      )
      .then((res) => res.data),

  // GET /terminology/standard-terms/:id
  getStandardTerm: (id: number): Promise<StandardTerm> =>
    apiClient
      .get<unknown, { data: StandardTerm }>(`/terminology/standard-terms/${id}`)
      .then((res) => res.data),

  // POST /terminology/standard-terms
  createStandardTerm: (body: StandardTermUpsertRequest): Promise<StandardTerm> =>
    apiClient
      .post<unknown, { data: StandardTerm }>('/terminology/standard-terms', body)
      .then((res) => res.data),

  // PUT /terminology/standard-terms/:id
  updateStandardTerm: (
    id: number,
    body: StandardTermUpsertRequest
  ): Promise<StandardTerm> =>
    apiClient
      .put<unknown, { data: StandardTerm }>(`/terminology/standard-terms/${id}`, body)
      .then((res) => res.data),

  // DELETE /terminology/standard-terms/:id?mode=DEPRECATE
  deleteStandardTerm: (
    id: number,
    mode: 'DEPRECATE' | 'SOFT_DELETE' = 'DEPRECATE'
  ): Promise<void> =>
    apiClient
      .delete(`/terminology/standard-terms/${id}?mode=${mode}`)
      .then(() => undefined),

  // ─── Forbidden Words ────────────────────────────────────────────────────

  // GET /terminology/forbidden-words
  listForbiddenWords: (
    params: ForbiddenWordSearchParams = {}
  ): Promise<PageResponse<ForbiddenWord>> =>
    apiClient
      .get<unknown, { data: PageResponse<ForbiddenWord> }>(
        `/terminology/forbidden-words${toQueryString(params as Record<string, unknown>)}`
      )
      .then((res) => res.data),

  // GET /terminology/forbidden-words/:id
  getForbiddenWord: (id: number): Promise<ForbiddenWord> =>
    apiClient
      .get<unknown, { data: ForbiddenWord }>(`/terminology/forbidden-words/${id}`)
      .then((res) => res.data),

  // POST /terminology/forbidden-words
  createForbiddenWord: (body: ForbiddenWordUpsertRequest): Promise<ForbiddenWord> =>
    apiClient
      .post<unknown, { data: ForbiddenWord }>('/terminology/forbidden-words', body)
      .then((res) => res.data),

  // PUT /terminology/forbidden-words/:id
  updateForbiddenWord: (
    id: number,
    body: ForbiddenWordUpsertRequest
  ): Promise<ForbiddenWord> =>
    apiClient
      .put<unknown, { data: ForbiddenWord }>(`/terminology/forbidden-words/${id}`, body)
      .then((res) => res.data),

  // DELETE /terminology/forbidden-words/:id?mode=DEPRECATE
  deleteForbiddenWord: (
    id: number,
    mode: 'DEPRECATE' | 'SOFT_DELETE' = 'DEPRECATE'
  ): Promise<void> =>
    apiClient
      .delete(`/terminology/forbidden-words/${id}?mode=${mode}`)
      .then(() => undefined),

  // ─── Summary ────────────────────────────────────────────────────────────

  // GET /terminology/summary
  getSummary: (): Promise<TerminologySummary> =>
    apiClient
      .get<unknown, { data: TerminologySummary }>('/terminology/summary')
      .then((res) => res.data),

  // ─── enhance-biz #632 — 공개 export + starter pack ──────────────────────

  // GET /terminology/export?format=&type=&group=&status=
  exportTerminology: (params: TerminologyExportParams): Promise<TerminologyDownloadResult> => {
    const fallback = `${params.type === 'forbidden' ? 'forbidden_words' : 'standard_terms'}.${params.format}`;
    const qs = toQueryString(params as unknown as Record<string, unknown>);
    return fetchBlob(`/terminology/export${qs}`, fallback);
  },

  // GET /terminology/starter-pack
  downloadStarterPack: (): Promise<TerminologyDownloadResult> =>
    fetchBlob('/terminology/starter-pack', 'terminology-starter-pack.zip'),

  // ─── enhance-biz #661 — 온라인 검사기 ─────────────────────────────────────

  // POST /terminology/lint-reports (transient — 서버 미영속)
  lintText: (body: LintRequest): Promise<LintReportResponse> =>
    apiClient
      .post<unknown, { data: LintReportResponse }>('/terminology/lint-reports', body)
      .then((res) => res.data),
};
